<template>
  <div>
    <div v-if="file" class='file-container'
         :style="{backgroundColor: colors.userInput.text, color: colors.userInput.bg}">
      <span class='icon-file-message'>
        <img :src="icons.file.img" :alt="icons.file.name" height="15"/>
      </span>
      {{ fileName }}
      <span class='delete-file-message' @click="cancelFile()">
        <v-icon name="times" :title="$t('interface.removeFile')" :style="{color: colors.userInput.bg}"/>
      </span>
      <div v-if="fileTooLarge">
        ⚠️ {{ $t('interface.maxFileSize') }}
      </div>
    </div>
    <form class="sc-user-input-wrapper-container"
          :class="{ active: inputActive, 'powered-by-amio': poweredByAmioConfig.enabled }"
          :style="{ background: colors.userInput.wrapperBg }"
    >
      <div class="sc-user-input-wrapper">
        <div class="sc-user-input" :style="{background: colors.userInput.bg}">
          <div
            role="button"
            tabIndex="0"
            @focus="setInputActive(true)"
            @blur="setInputActive(false)"
            @keydown.enter.exact.prevent="handleSubmit"
            @paste="handlePaste"
            @input="containsInputText = !!$event.target.textContent"
            :contentEditable="!isVoiceUploadLoading && !isAudioRecording"
            :placeholder="$t('interface.writeMessage')"
            class="sc-user-input--text"
            ref="userInput"
            :style="{color: colors.userInput.text}"
          />
          <div class="sc-user-input--buttons">
            <user-input-button v-if="containsInputText && !isAudioRecording"
                              :icon-color="colors.userInput.text"
                              tooltip="Clear"
                              icon-offset-y="4px"
                              @click.prevent="clearInput"
            >
              <icon-clear/>
            </user-input-button>
            <div v-else-if="showFile && !isAudioRecording" class="sc-user-input--button">
              <file-icons :onChange="_handleFileSubmit"
                          :color="colors.userInput.text"
                          :title="$t('interface.attachFile')"/>
            </div>
            <div v-else class="sc-user-input--button">
              <icon-sound-wave/>
            </div>
          </div>
        </div>
        <user-input-control :audio-recording="isAudioRecording"
                            :colors="colors"
                            :contains-input-text="containsInputText"
                            :submit-disabled="submitDisabled"
                            :show-voice="showVoice"
                            :is-sound-detected="isSoundDetected"
                            :is-button-disabled="isButtonDisabled"
                            :is-microphone-access-denied="isMicrophoneAccessDenied"
                            @submit="handleSubmit"
        />
      </div>
      <powered-by-amio v-if="poweredByAmioConfig.enabled && !colors.launcher.bg.includes('gradient')" :amio-color="colors.launcher.bg" :utm-source="poweredByAmioConfig.utmSource"/>
      <powered-by-amio v-else-if="poweredByAmioConfig.enabled && colors.launcher.bg.includes('gradient')" :amio-color="colors.sentMessage.bg" :utm-source="poweredByAmioConfig.utmSource"/>
    </form>
  </div>
</template>

<script>
import FileIcons from './icons/FileIcons.vue'
import UserInputButton from './UserInputButton.vue'
import FileIcon from '../../assets/file.svg'
import stringUtils from '../../util/string-utils'
import { EventBus, Event } from '@/util/event-bus'
import IconClear from './icons/icons/IconClear'
import 'vue-awesome/icons/times'
import UserInputControl from '@/components/chat/UserInputControl'
import { ANIMATION_TIME } from '@/util/constants'
import IconSoundWave from '@/components/chat/icons/icons/IconSoundWave'
import PoweredByAmio from '@/components/chat/PoweredByAmio'

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB

export default {

  components: {
    IconSoundWave,
    UserInputControl,
    UserInputButton,
    FileIcons,
    IconClear,
    PoweredByAmio
  },
  props: {
    icons: {
      type: Object,
      required: false,
      default: function () {
        return {
          file: {
            img: FileIcon,
            name: 'default'
          }
        }
      }
    },
    showFile: {
      type: Boolean,
      default: () => false
    },
    showVoice: {
      type: Boolean,
      default: false
    },
    onSubmit: {
      type: Function,
      required: true
    },
    onButtonPressed: {
      type: Function,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Write something...'
    },
    colors: {
      type: Object,
      required: true
    },
    poweredByAmioConfig: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      file: null,
      inputActive: false,
      online: false,
      audioRecorder: null,
      containsInputText: false,
      voiceService: null,
      voiceServiceInitPromise: null
    }
  },
  watch: {
    showVoice: {
      immediate: true,
      handler (enabled) {
        if (enabled) this.initVoiceService()
      }
    }
  },
  methods: {
    // Lazy load voice service to avoid loading the large WASM files until it's needed
    async initVoiceService () {
      if (!this.showVoice || this.voiceService) return
      if (this.voiceServiceInitPromise) return this.voiceServiceInitPromise

      this.voiceServiceInitPromise = import('@/util/voice.service')
        .then(({ default: VoiceService }) => {
          this.voiceService = new VoiceService(navigator)
        })
        .catch(() => {
          this.voiceService = null
        })
        .finally(() => {
          this.voiceServiceInitPromise = null
        })

      return this.voiceServiceInitPromise
    },
    cancelFile () {
      this.file = null
    },
    setInputActive (onoff) {
      this.inputActive = onoff
    },
    async focusUserInput () {
      await this.$nextTick()
      if (!this.$refs.userInput) return

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      if (isIOS) {
        // For iOS, only focus when user touches the screen
        const focusHandler = () => {
          this.$refs.userInput.focus()
          document.removeEventListener('touchend', focusHandler)
        }
        document.addEventListener('touchend', focusHandler)
      } else {
        // For other systems, allow autofocus
        this.$refs.userInput.focus()
      }
    },

    /**
     * HACK: since the chat is using div with contenteditable instead of input/textarea,
     * it would insert formatted html when something is pasted. This function extracts
     * only text from whatever was pasted. The reason we didn't just use input/textarea
     * instead of the div is that it's resized automatically when a long text is written,
     * which is hard to achieve with textarea
     */
    handlePaste (e) {
      e.preventDefault()
      let text = ''
      if (e.clipboardData || e.originalEvent.clipboardData) {
        text = (e.originalEvent || e).clipboardData.getData('text/plain')
      } else if (window.clipboardData) {
        text = window.clipboardData.getData('Text')
      }

      const command = document.queryCommandSupported('insertText') ? 'insertText' : 'paste'
      document.execCommand(command, false, text)
    },
    submitMessage (event) {
      if (this.submitDisabled) return

      const text = this.$refs.userInput.innerText.trim()
      const file = this.file
      if (file) {
        this._submitTextWhenFile(event, text, file)
      } else {
        if (text && text.length > 0) {
          this.onSubmit(this.createClientMessage('text', { text }))
          this.$refs.userInput.innerHTML = ''
        }
      }
      this.containsInputText = false
    },
    _submitTextWhenFile (event, text, file) {
      this.onSubmit(this.createClientMessage('file', {
        file,
        url: null
      }))
      this.file = null

      if (text && text.length > 0) {
        this.onSubmit(this.createClientMessage('text', { text }))
        this.$refs.userInput.innerHTML = ''
      }
    },
    _handleFileSubmit (file) {
      this.file = file
    },
    createClientMessage (type, data) {
      return {
        author: 'client',
        direction: 'sent',
        type,
        data,
        error: null
      }
    },

    clearInput () {
      if (this.isVoiceUploadLoading) return

      this.$refs.userInput.textContent = ''
      this.focusUserInput()
      this.containsInputText = false
    },

    async handleSubmit (e) {
      if ((!this.containsInputText || this.isAudioRecording) && this.showVoice) {
        if (!this.voiceService) await this.initVoiceService()
        if (this.voiceService) await this.voiceService.handleAudioRecording()
        return
      }

      if (this.isVoiceUploadLoading) return

      this.submitMessage(e)
    },

    setMessage (text) {
      this.containsInputText = !!text
      if (!text) return

      this.$refs.userInput.textContent = text
      this.focusUserInput()
    }
  },

  computed: {
    isVoiceUploadLoading () {
      return this.voiceService?.voiceUploadLoading
    },

    isContentEditable () {
      return !this.isVoiceUploadLoading && !this.isAudioRecording
    },

    isAudioRecording () {
      return this.voiceService?.audioRecording
    },

    isMicrophoneAccessDenied () {
      return this.voiceService?.isMicrophoneAccessDenied
    },

    isSoundDetected () {
      return this.voiceService?.isSoundDetected
    },

    isButtonDisabled () {
      return this.isVoiceUploadLoading && !this.isAudioRecording
    },

    fileTooLarge () {
      return !!(this.file && this.file.size > MAX_FILE_SIZE)
    },

    submitDisabled () {
      return this.fileTooLarge || !this.online
    },

    fileName () {
      return stringUtils.ellipsis(this.file.name, 30)
    }
  },

  mounted () {
    EventBus.$on(Event.INPUT_FOCUS, () => {
      setTimeout(() => this.focusUserInput(), ANIMATION_TIME)
    })
    EventBus.$on(Event.CHAT_ONLINE, () => {
      this.online = true
    })
    EventBus.$on(Event.CHAT_OFFLINE, () => {
      this.online = false
    })
    EventBus.$on(Event.DICTATION_UPDATE, dictation => this.setMessage(dictation))
  }
}
</script>
